import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'

interface Status {
  id: string
  label: string
  color: string
}

interface Props {
  value: string
  statuses: Status[]
  onChange: (value: string) => void
  className?: string
}

export function StatusSelect({ value, statuses, onChange, className = '' }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = statuses.find(s => s.label === value)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input flex items-center justify-between gap-2 w-full text-left"
      >
        <span className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: current?.color ?? '#6b7280' }}
          />
          <span>{value || 'Select status'}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {statuses.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => { onChange(s.label); setOpen(false) }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 text-left transition-colors ${s.label === value ? 'bg-gray-50 font-medium' : ''}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
